/**
 * Ordered-list editor for responsibilities / requirements / nice-to-have /
 * benefits (PRD §7.3: add, edit, remove, reorder). Reuses the builder block
 * list interaction vocabulary: simple rows with move up/down + remove —
 * keyboard-accessible, no drag required.
 */
import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react-native';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { useHaptics } from '../../../hooks/useHaptics';
import { moveListItem } from './post-job-machine';
import { FieldLabel, InlineError } from './post-job-ui';

interface OrderedListEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  errorKey?: string | null;
}

export function OrderedListEditor({
  label,
  items,
  onChange,
  placeholder,
  errorKey = null,
}: OrderedListEditorProps) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const value = draft.trim();
    if (!value) return;
    haptics.trigger('light');
    onChange([...items, value]);
    setDraft('');
  };

  const move = (index: number, direction: -1 | 1) => {
    haptics.trigger('selection');
    onChange(moveListItem(items, index, index + direction));
  };

  return (
    <View style={styles.group}>
      <FieldLabel>{label}</FieldLabel>

      {items.length > 0 ? (
        <View style={styles.list}>
          {items.map((item, index) => (
            <View
              key={`${index}-${items.length}`}
              style={[
                styles.row,
                { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
              ]}
            >
              <TextInput
                value={item}
                onChangeText={(value) => {
                  const next = [...items];
                  next[index] = value;
                  onChange(next);
                }}
                multiline
                style={[styles.rowInput, { color: theme.text }]}
                accessibilityLabel={`${label} ${index + 1}`}
              />
              <View style={styles.rowActions}>
                <Pressable
                  onPress={() => move(index, -1)}
                  disabled={index === 0}
                  hitSlop={6}
                  accessibilityLabel={t('jobFlow.list.moveUp') as string}
                  style={[styles.iconButton, index === 0 && styles.iconDisabled]}
                >
                  <ChevronUp size={15} color={theme.textTertiary} strokeWidth={2.2} />
                </Pressable>
                <Pressable
                  onPress={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  hitSlop={6}
                  accessibilityLabel={t('jobFlow.list.moveDown') as string}
                  style={[
                    styles.iconButton,
                    index === items.length - 1 && styles.iconDisabled,
                  ]}
                >
                  <ChevronDown size={15} color={theme.textTertiary} strokeWidth={2.2} />
                </Pressable>
                <Pressable
                  onPress={() => {
                    haptics.trigger('light');
                    onChange(items.filter((_, itemIndex) => itemIndex !== index));
                  }}
                  hitSlop={6}
                  accessibilityLabel={t('jobFlow.list.remove') as string}
                  style={styles.iconButton}
                >
                  <X size={15} color={theme.textTertiary} strokeWidth={2.2} />
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <Text style={[styles.emptyHint, { color: theme.textTertiary }]}>
          {t('jobFlow.list.empty')}
        </Text>
      )}

      <View style={styles.addRow}>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          placeholder={placeholder}
          placeholderTextColor={theme.textTertiary}
          onSubmitEditing={addItem}
          blurOnSubmit={false}
          style={[
            styles.addInput,
            {
              borderColor: theme.border,
              backgroundColor: theme.background,
              color: theme.text,
            },
          ]}
        />
        <Pressable
          onPress={addItem}
          accessibilityLabel={t('jobFlow.list.add') as string}
          style={[styles.addButton, { backgroundColor: theme.buttonSoftBg }]}
        >
          <Plus size={18} color={colors.primary} strokeWidth={2.5} />
        </Pressable>
      </View>

      <InlineError messageKey={errorKey} />
    </View>
  );
}

const styles = StyleSheet.create({
  group: {
    gap: spacing[2],
  },
  list: {
    gap: spacing[2],
  },
  row: {
    borderWidth: 1,
    borderRadius: borderRadius.xl,
    paddingStart: spacing[4],
    paddingEnd: spacing[2],
    paddingVertical: spacing[2],
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  rowInput: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: spacing[1],
    padding: 0,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconDisabled: {
    opacity: 0.35,
  },
  emptyHint: {
    fontSize: 12,
    lineHeight: 18,
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  addInput: {
    flex: 1,
    minHeight: 48,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing[5],
    paddingVertical: 12,
    fontSize: 15,
  },
  addButton: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
